import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Download,
  MapPin,
  Clock,
  Calendar,
  Building,
  Users,
  Phone,
  ExternalLink,
  RefreshCw,
  ArrowLeft,
  Globe,
  Mail,
  DatabaseZap,
  CheckCircle,
  Home,
  MessageCircle,
  BarChart3,
  Send,
  Megaphone,
  Smartphone,
  Monitor,
  TrendingUp,
  Search,
  Award,
  Zap,
  Loader2,
  RotateCcw,
  Pencil,
  Check,
  X,
  FileCode,
  Save,
  BarChart2,
  MousePointerClick,
  Eye,
  Percent,
  Link2
} from "lucide-react";

// Reusable components
import AnalyticsDashboard from "@/components/analytics/AnalyticsDashboard";

// Default BNI Email Templates
const DEFAULT_BNI_EMAIL_TEMPLATES = {
  first: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #2563eb; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .cta-button { display: inline-block; background: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Opportunità di Collaborazione</h1>
    </div>
    <div class="content">
      <p>Gentile <strong>{{NOME}} {{COGNOME}}</strong>,</p>
      <p>Mi chiamo Max Valle e faccio parte del capitolo BNI <strong>{{CAPITOLO}}</strong>.</p>
      <p>Ho visitato il vostro sito web e credo che potremmo trovare interessanti opportunità di collaborazione.</p>
      <p><a href="{{LINK_CALENDAR_BNI}}" class="cta-button">Prenota un appuntamento</a></p>
      <p>Cordiali saluti,<br><strong>Max Valle</strong></p>
    </div>
    <div class="footer">
      <p>Marketing Max Valle</p>
    </div>
  </div>
</body>
</html>`,
  second: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #059669; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .cta-button { display: inline-block; background: #059669; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Follow-up</h1>
    </div>
    <div class="content">
      <p>Gentile <strong>{{NOME}} {{COGNOME}}</strong>,</p>
      <p>Ti scrivo per dare seguito alla mia precedente email riguardo una possibile collaborazione.</p>
      <p>Saresti disponibile per una breve chiamata questa settimana?</p>
      <p><a href="{{LINK_CALENDAR_BNI}}" class="cta-button">Scegli un orario</a></p>
      <p>Grazie,<br><strong>Max Valle</strong><br>BNI {{CAPITOLO}}</p>
    </div>
    <div class="footer">
      <p>Marketing Max Valle</p>
    </div>
  </div>
</body>
</html>`,
  third: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #dc2626; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; }
    .footer { background: #1f2937; color: #9ca3af; padding: 15px; text-align: center; font-size: 12px; border-radius: 0 0 8px 8px; }
    .cta-button { display: inline-block; background: #dc2626; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Ultimo messaggio</h1>
    </div>
    <div class="content">
      <p>Gentile <strong>{{NOME}} {{COGNOME}}</strong>,</p>
      <p>Questo sarà il mio ultimo messaggio. Se in futuro dovessi essere interessato, sarò felice di sentirti.</p>
      <p><a href="{{LINK_CALENDAR_BNI}}" class="cta-button">Prenota quando vuoi</a></p>
      <p>Ti auguro il meglio,<br><strong>Max Valle</strong><br>BNI {{CAPITOLO}}</p>
    </div>
    <div class="footer">
      <p>Marketing Max Valle</p>
    </div>
  </div>
</body>
</html>`
};

const DEFAULT_BNI_WHATSAPP_TEMPLATES = {
  first: `Ciao {{NOME}}! 👋

Sono Max Valle del capitolo BNI {{CAPITOLO}}.

Ho visitato il tuo sito web e mi piacerebbe parlare di possibili collaborazioni.

Ti va di fare una breve call? Puoi prenotare qui: {{LINK_CALENDAR_BNI}}

A presto!`,
  second: `Ciao {{NOME}}! 👋

Ti ho scritto qualche giorno fa riguardo una possibile collaborazione.

Hai 15 minuti liberi questa settimana? {{LINK_CALENDAR_BNI}}

Grazie! 🙏`,
  third: `Ciao {{NOME}},

Ti scrivo un'ultima volta per non essere troppo insistente.

Se in futuro sei interessato/a, il link è sempre attivo: {{LINK_CALENDAR_BNI}}

Ti auguro il meglio! 🍀
Max Valle - BNI {{CAPITOLO}}`
};

const DEFAULT_BNI_EMAIL_SUBJECTS = {
  first: "Opportunità di collaborazione - BNI {{CAPITOLO}}",
  second: "Follow-up: Opportunità di collaborazione",
  third: "Ultimo messaggio - Collaborazione BNI"
};

const ChapterDetailPage = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const chapterUrl = searchParams.get("url");
  
  const [isLoading, setIsLoading] = useState(false);
  const [isExtractingData, setIsExtractingData] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(null); // { message, processed, total, emails, websites }
  const [chapter, setChapter] = useState(null);
  const [members, setMembers] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("members");
  const [settings, setSettings] = useState(null);
  
  // SEO Analysis state
  const [seoData, setSeoData] = useState({});
  const [analyzingMember, setAnalyzingMember] = useState(null);
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  
  // Marketing tracking state
  const [marketingStats, setMarketingStats] = useState({});
  
  // Member editing state
  const [editingMember, setEditingMember] = useState(null); // { id, field, value }
  const [isSavingMember, setIsSavingMember] = useState(false);
  
  // BNI Template state
  const [bniEmailTemplates, setBniEmailTemplates] = useState(DEFAULT_BNI_EMAIL_TEMPLATES);
  const [bniWhatsappTemplates, setBniWhatsappTemplates] = useState(DEFAULT_BNI_WHATSAPP_TEMPLATES);
  const [bniEmailSubjects, setBniEmailSubjects] = useState(DEFAULT_BNI_EMAIL_SUBJECTS);
  const [activeTemplateType, setActiveTemplateType] = useState("email");
  const [activeTemplateStage, setActiveTemplateStage] = useState("first");
  const [isSavingTemplates, setIsSavingTemplates] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(null); // member ID being sent
  
  // Custom links state (for tracking)
  const [customLinks, setCustomLinks] = useState({
    tracking: "",
    video: "",
    consulenza: "",
    risorsa: "",
    chiamami: ""
  });
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  // Helper function to extract error message safely (handles Pydantic validation errors)
  const getErrorMessage = (error, fallback = "Errore") => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
    return fallback;
  };

  // Load cached SEO data when members change
  useEffect(() => {
    const loadCachedSeoData = async () => {
      if (!token || members.length === 0) return;
      
      try {
        const response = await axios.get(
          `${API}/seo/cached`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const cachedDomains = response.data.cached_domains || [];
        const cachedMap = {};
        
        cachedDomains.forEach(item => {
          if (item.domain) {
            cachedMap[item.domain] = item;
          }
        });
        
        // Only set if we have cached data
        if (Object.keys(cachedMap).length > 0) {
          setSeoData(prev => ({ ...cachedMap, ...prev }));
        }
      } catch (error) {
        console.error("Error loading cached SEO data:", error);
      }
    };
    
    loadCachedSeoData();
  }, [token, members]);

  // Load marketing stats for chapter
  useEffect(() => {
    const loadMarketingStats = async () => {
      if (!token || !chapter?.chapter_id) return;
      
      try {
        const response = await axios.get(
          `${API}/marketing/stats/${chapter.chapter_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const statsMap = {};
        (response.data.stats || []).forEach(stat => {
          statsMap[stat.member_id] = stat;
        });
        setMarketingStats(statsMap);
      } catch (error) {
        console.error("Error loading marketing stats:", error);
      }
    };
    
    loadMarketingStats();
  }, [token, chapter]);

  // Track marketing action
  const trackMarketingAction = async (member, actionType, stage) => {
    try {
      // Use encrypted_member_id as the primary identifier
      const memberId = member.encrypted_member_id || member.member_id || member.id;
      
      const response = await axios.post(
        `${API}/marketing/track`,
        {
          member_id: memberId,
          chapter_id: chapter.chapter_id,
          action_type: actionType,
          stage: stage,
          member_name: member.name,
          member_email: member.email,
          member_phone: member.phone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setMarketingStats(prev => ({
        ...prev,
        [memberId]: {
          ...prev[memberId],
          email_count: response.data.email_count,
          whatsapp_count: response.data.whatsapp_count
        }
      }));
      
      toast.success(`${actionType === 'email' ? 'Email' : 'WhatsApp'} registrato!`);
    } catch (error) {
      console.error("Error tracking marketing action:", error);
      toast.error("Errore nel tracciamento");
    }
  };

  // Normalize domain for display
  const normalizeDomain = (website) => {
    if (!website) return "";
    let domain = website.toLowerCase().trim();
    domain = domain.replace(/^https?:\/\//, '');
    domain = domain.replace(/^www\./, '');
    domain = domain.split('/')[0];
    domain = domain.split('?')[0];
    domain = domain.split('#')[0];
    return domain;
  };

  // Analyze single website SEO
  const analyzeSingleWebsite = async (website, forceRefresh = false) => {
    const domain = normalizeDomain(website);
    if (!domain) {
      console.warn("Invalid website for SEO analysis:", website);
      toast.error("URL sito non valido");
      return;
    }
    
    // Skip if already has valid data and not forcing refresh
    if (!forceRefresh && seoData[domain] && hasSeoData(seoData[domain])) {
      console.log("SEO data already exists for:", domain);
      return;
    }
    
    console.log("Starting SEO analysis for:", domain);
    setAnalyzingMember(domain);
    
    try {
      const response = await axios.post(
        `${API}/seo/analyze`,
        { website, force_refresh: forceRefresh },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }  // 3 minutes timeout
      );
      console.log("SEO analysis completed for:", domain, response.data);
      setSeoData(prev => ({ ...prev, [domain]: response.data }));
      if (forceRefresh) {
        toast.success(`Analisi SEO aggiornata per ${domain}`);
      }
    } catch (error) {
      console.error("SEO analysis error for", domain, ":", error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error(`Timeout analisi ${domain} - il sito potrebbe essere lento`);
      } else if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
      } else {
        toast.error(`Errore analisi ${domain}`);
      }
      setSeoData(prev => ({ ...prev, [domain]: { error: true, errors: [error.message || 'Errore sconosciuto'] } }));
    } finally {
      setAnalyzingMember(null);
    }
  };

  // Check if SEO data has actual values (not all null)
  const hasSeoData = (seo) => {
    if (!seo) return false;
    if (seo.error) return false;
    
    // Check if at least one value is not null/undefined
    // Note: 0 is a valid value!
    const hasPagespeedMobile = seo.pagespeed_mobile !== null && seo.pagespeed_mobile !== undefined;
    const hasPagespeedDesktop = seo.pagespeed_desktop !== null && seo.pagespeed_desktop !== undefined;
    const hasTraffic = seo.traffic !== null && seo.traffic !== undefined;
    const hasKeywords = seo.keywords_total !== null && seo.keywords_total !== undefined;
    const hasAuthority = seo.authority !== null && seo.authority !== undefined;
    
    return hasPagespeedMobile || hasPagespeedDesktop || hasTraffic || hasKeywords || hasAuthority;
  };

  // Check if SEO analysis failed (all null with errors)
  const hasSeoErrors = (seo) => {
    if (!seo || !seo.domain) return false;
    return seo.errors && seo.errors.length > 0 && !hasSeoData(seo);
  };

  // Extract only the specialization part (after ">")
  const getShortSpecialization = (spec) => {
    if (!spec) return "-";
    // If contains ">", take only the part after the last ">"
    if (spec.includes(">")) {
      const parts = spec.split(">");
      return parts[parts.length - 1].trim();
    }
    return spec;
  };

  // Analyze all websites with valid domains
  const analyzeAllWebsites = async () => {
    const validMembers = members.filter(m => isValidWebsite(m.website));
    if (validMembers.length === 0) {
      toast.warning("Nessun sito web valido da analizzare");
      return;
    }
    
    // Filter only members that need analysis
    const membersToAnalyze = validMembers.filter(m => {
      const domain = normalizeDomain(m.website);
      return !seoData[domain] || !hasSeoData(seoData[domain]);
    });
    
    if (membersToAnalyze.length === 0) {
      toast.info("Tutti i siti sono già stati analizzati");
      return;
    }
    
    setIsAnalyzingAll(true);
    toast.info(`Analisi SEO di ${membersToAnalyze.length} siti in corso... (puoi uscire, riprenderà automaticamente)`);
    
    let analyzed = 0;
    let errors = 0;
    
    // Save progress key for this chapter
    const progressKey = `seo_progress_${chapter?.chapter_id}`;
    
    for (const member of membersToAnalyze) {
      // Check if user cancelled
      if (!isAnalyzingAll) break;
      
      try {
        const domain = normalizeDomain(member.website);
        setAnalyzingMember(domain);
        
        // Save current progress to localStorage
        localStorage.setItem(progressKey, JSON.stringify({
          currentDomain: domain,
          analyzed,
          total: membersToAnalyze.length,
          timestamp: Date.now()
        }));
        
        const response = await axios.post(
          `${API}/seo/analyze`,
          { website: member.website, force_refresh: false },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
        );
        
        setSeoData(prev => ({ ...prev, [domain]: response.data }));
        analyzed++;
        
        // Update progress every 3 sites
        if (analyzed % 3 === 0) {
          toast.info(`Analizzati ${analyzed}/${membersToAnalyze.length} siti...`);
        }
      } catch (error) {
        console.error(`SEO analysis error for ${member.website}:`, error);
        errors++;
        const domain = normalizeDomain(member.website);
        setSeoData(prev => ({ ...prev, [domain]: { error: true, errors: [error.message || 'Errore'] } }));
      }
      
      // Small delay between requests to avoid rate limiting
      await new Promise(r => setTimeout(r, 300));
    }
    
    // Clear progress from localStorage
    localStorage.removeItem(progressKey);
    
    setAnalyzingMember(null);
    setIsAnalyzingAll(false);
    
    if (errors > 0) {
      toast.warning(`Analisi completata: ${analyzed} OK, ${errors} errori`);
    } else {
      toast.success(`Analisi SEO completata: ${analyzed} siti analizzati!`);
    }
  };

  // Get SEO score color
  const getScoreColor = (score) => {
    if (score === null || score === undefined) return "text-gray-400";
    if (score >= 90) return "text-green-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  // Format large numbers
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
  };

  // Standardize phone for WhatsApp
  const standardizePhoneForWhatsApp = (phone) => {
    if (!phone) return "";
    let clean = phone.replace(/[^\d+]/g, '');
    
    if (clean.startsWith('+39')) {
      clean = '+39' + clean.slice(3).replace(/^0+/, '');
    } else if (clean.startsWith('39') && clean.length > 10) {
      clean = '+39' + clean.slice(2).replace(/^0+/, '');
    } else if (clean.startsWith('00')) {
      clean = '+' + clean.slice(2);
    } else if (clean.startsWith('3') && clean.length === 10) {
      clean = '+39' + clean;
    } else if (clean.startsWith('0') && clean.length >= 9) {
      clean = '+39' + clean.slice(1);
    }
    
    return clean;
  };

  // Parse member name into first and last name
  const parseName = (fullName) => {
    if (!fullName) return { firstName: "", lastName: "" };
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(" ")
    };
  };

  // Generate WhatsApp link
  const getWhatsAppLink = (member) => {
    const phone = standardizePhoneForWhatsApp(member.phone);
    if (!phone) return null;
    
    let message = settings?.whatsapp_template || "";
    const { firstName, lastName } = parseName(member.name);
    
    message = message
      .replace(/\{\{NOME\}\}/g, firstName)
      .replace(/\{\{COGNOME\}\}/g, lastName)
      .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "")
      .replace(/\{\{LINK_CALENDAR\}\}/g, settings?.calendar_link || "")
      .replace(/\{\{LINK_ANALISI_SITO\}\}/g, `#analisi-${member.encrypted_member_id || ""}`);
    
    return `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(message)}`;
  };

  // Generate Email link
  const getEmailLink = (member) => {
    if (!member.email) return null;
    
    let subject = settings?.email_subject || "Opportunità di collaborazione";
    const { firstName, lastName } = parseName(member.name);
    
    subject = subject
      .replace(/\{\{NOME\}\}/g, firstName)
      .replace(/\{\{COGNOME\}\}/g, lastName)
      .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "");
    
    // For email body, we'll use plain text version
    let body = "Gentile " + member.name + ",\n\n";
    body += "Mi chiamo Max Valle e faccio parte del capitolo BNI " + (chapter?.name || "") + ".\n\n";
    body += "Mi piacerebbe organizzare una breve call conoscitiva per esplorare eventuali sinergie.\n\n";
    if (settings?.calendar_link) {
      body += "Prenota un appuntamento: " + settings.calendar_link + "\n\n";
    }
    body += "Cordiali saluti";
    
    return `mailto:${member.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Extract chapter_id from URL
  const extractChapterId = (url) => {
    if (!url) return null;
    const decodedUrl = decodeURIComponent(url);
    const match = decodedUrl.match(/chapterId=([^&]+)/);
    return match ? match[1] : null;
  };

  const loadChapterDetail = async () => {
    if (!chapterUrl) {
      toast.error("URL del capitolo non valido");
      return;
    }

    setIsLoading(true);
    const chapterId = extractChapterId(chapterUrl);
    
    try {
      // STEP 1: Try to load from database first (persistent data)
      if (chapterId) {
        try {
          const savedResponse = await axios.get(
            `${API}/chapter/${encodeURIComponent(chapterId)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (savedResponse.data.chapter && savedResponse.data.members?.length > 0) {
            setChapter(savedResponse.data.chapter);
            setMembers(savedResponse.data.members);
            setHasLoaded(true);
            
            // Load custom links if present
            if (savedResponse.data.chapter.custom_links) {
              setCustomLinks({
                tracking: savedResponse.data.chapter.custom_links.tracking || "",
                video: savedResponse.data.chapter.custom_links.video || "",
                consulenza: savedResponse.data.chapter.custom_links.consulenza || "",
                risorsa: savedResponse.data.chapter.custom_links.risorsa || "",
                chiamami: savedResponse.data.chapter.custom_links.chiamami || ""
              });
            }
            
            toast.success(`Caricati ${savedResponse.data.members.length} membri dal database`);
            return; // Data found in DB, no need to scrape
          }
        } catch (dbError) {
          // Chapter not in DB, will scrape below
          console.log("Chapter not in database, will fetch from BNI website");
        }
      }

      // STEP 2: If not in DB, scrape from BNI website
      const response = await axios.post(
        `${API}/chapter/detail`,
        { chapter_url: decodeURIComponent(chapterUrl) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setChapter(response.data.chapter);
      setMembers(response.data.members || []);
      setHasLoaded(true);
      
      if (response.data.members?.length > 0) {
        toast.success(`Estratti ${response.data.members.length} membri dal sito BNI`);
      }
    } catch (error) {
      console.error("Error loading chapter:", error);
      toast.error("Errore nel caricamento del capitolo");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      
      // Load BNI templates from settings
      if (response.data.email_templates) {
        setBniEmailTemplates({
          first: response.data.email_templates.first || DEFAULT_BNI_EMAIL_TEMPLATES.first,
          second: response.data.email_templates.second || DEFAULT_BNI_EMAIL_TEMPLATES.second,
          third: response.data.email_templates.third || DEFAULT_BNI_EMAIL_TEMPLATES.third
        });
      }
      if (response.data.whatsapp_templates) {
        setBniWhatsappTemplates({
          first: response.data.whatsapp_templates.first || DEFAULT_BNI_WHATSAPP_TEMPLATES.first,
          second: response.data.whatsapp_templates.second || DEFAULT_BNI_WHATSAPP_TEMPLATES.second,
          third: response.data.whatsapp_templates.third || DEFAULT_BNI_WHATSAPP_TEMPLATES.third
        });
      }
      if (response.data.email_subjects) {
        setBniEmailSubjects({
          first: response.data.email_subjects.first || DEFAULT_BNI_EMAIL_SUBJECTS.first,
          second: response.data.email_subjects.second || DEFAULT_BNI_EMAIL_SUBJECTS.second,
          third: response.data.email_subjects.third || DEFAULT_BNI_EMAIL_SUBJECTS.third
        });
      }
    } catch (error) {
      console.log("No settings found");
    }
  };
  
  const handleSaveBniTemplates = async () => {
    setIsSavingTemplates(true);
    try {
      await axios.post(`${API}/settings`, {
        email_templates: bniEmailTemplates,
        whatsapp_templates: bniWhatsappTemplates,
        email_subjects: bniEmailSubjects,
        // Preserve other settings
        calendar_link_bni: settings?.calendar_link_bni || settings?.calendar_link || "",
        calendar_link_standard: settings?.calendar_link_standard || "",
        smtp_settings: settings?.smtp_settings,
        prospect_email_template: settings?.prospect_email_template
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template BNI salvati!");
    } catch (error) {
      console.error("Error saving templates:", error);
      toast.error("Errore nel salvataggio dei template");
    } finally {
      setIsSavingTemplates(false);
    }
  };
  
  const handleSaveCustomLinks = async () => {
    if (!chapter?.chapter_id) return;
    
    try {
      await axios.put(
        `${API}/chapters/${encodeURIComponent(chapter.chapter_id)}`,
        { custom_links: customLinks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Link personalizzati salvati!");
    } catch (error) {
      console.error("Error saving custom links:", error);
      toast.error("Errore nel salvataggio dei link");
    }
  };
  
  const loadAnalytics = async () => {
    if (!chapter?.chapter_id) return;
    
    setIsLoadingAnalytics(true);
    try {
      const response = await axios.get(
        `${API}/analytics/chapter/${encodeURIComponent(chapter.chapter_id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };
  
  const exportAnalytics = async () => {
    if (!chapter?.chapter_id) return;
    
    try {
      const response = await axios.get(
        `${API}/analytics/chapter/${encodeURIComponent(chapter.chapter_id)}/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bni_analytics_${chapter?.name || 'export'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Report esportato!");
    } catch (error) {
      console.error("Error exporting analytics:", error);
      toast.error("Errore nell'esportazione");
    }
  };
  
  const handleSendTestBniEmail = async () => {
    if (!testEmailRecipient) {
      toast.error("Inserisci un indirizzo email");
      return;
    }
    setIsSendingTestEmail(true);
    try {
      const calendarLink = settings?.calendar_link_bni || settings?.calendar_link || "#";
      const subject = bniEmailSubjects[activeTemplateStage]
        .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "Capitolo");
      const body = bniEmailTemplates[activeTemplateStage]
        .replace(/\{\{NOME\}\}/g, "Mario")
        .replace(/\{\{COGNOME\}\}/g, "Rossi")
        .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "Capitolo")
        .replace(/\{\{LINK_CALENDAR_BNI\}\}/g, calendarLink)
        .replace(/\{\{LINK_CALENDAR\}\}/g, calendarLink);
        
      await axios.post(`${API}/email/test`, {
        to_email: testEmailRecipient,
        subject,
        body
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email di test inviata!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'invio dell'email di test"));
    } finally {
      setIsSendingTestEmail(false);
    }
  };
  
  const handleSendRealEmail = async (member, stage) => {
    if (!member.email) {
      toast.error("Questo membro non ha un'email");
      return;
    }
    
    const memberId = member.encrypted_member_id || member.member_id || member._id;
    setIsSendingEmail(memberId);
    
    try {
      const calendarLink = settings?.calendar_link_bni || settings?.calendar_link || "#";
      
      // Get SEO data for this member
      const domain = member.website ? new URL(member.website.startsWith('http') ? member.website : `https://${member.website}`).hostname.replace('www.', '') : '';
      const seo = seoData[domain] || {};
      
      // Format SEO values - use correct field names from API response
      const pagespeedMobile = seo.pagespeed_mobile !== null && seo.pagespeed_mobile !== undefined 
        ? String(seo.pagespeed_mobile) : "N/A";
      const pagespeedDesktop = seo.pagespeed_desktop !== null && seo.pagespeed_desktop !== undefined 
        ? String(seo.pagespeed_desktop) : "N/A";
      // SEOZoom uses 'authority' and 'traffic' field names
      const seozoomAuthority = seo.authority !== null && seo.authority !== undefined
        ? String(seo.authority) : "N/A";
      const seozoomTraffic = seo.traffic !== null && seo.traffic !== undefined
        ? String(seo.traffic).replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "N/A";
      
      // Build analysis link
      const analysisLink = member.website ? `https://pagespeed.web.dev/report?url=${encodeURIComponent(member.website)}` : "#";
      
      const subject = bniEmailSubjects[stage]
        .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "Capitolo")
        .replace(/\{\{NOME\}\}/g, member.name?.split(' ')[0] || "")
        .replace(/\{\{COGNOME\}\}/g, member.name?.split(' ').slice(1).join(' ') || "")
        .replace(/\{\{EMAIL\}\}/g, member.email || "");
      
      // Build body but DON'T replace LINK_* placeholders (backend will do that with tracking)
      const body = bniEmailTemplates[stage]
        .replace(/\{\{NOME\}\}/g, member.name?.split(' ')[0] || "")
        .replace(/\{\{COGNOME\}\}/g, member.name?.split(' ').slice(1).join(' ') || "")
        .replace(/\{\{EMAIL\}\}/g, member.email || "")
        .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "Capitolo")
        .replace(/\{\{LINK_CALENDAR_BNI\}\}/g, calendarLink)
        .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/g, settings?.calendar_link_standard || calendarLink)
        .replace(/\{\{LINK_CALENDAR\}\}/g, calendarLink)
        .replace(/\{\{PAGESPEED_MOBILE\}\}/g, pagespeedMobile)
        .replace(/\{\{PAGESPEED_DESKTOP\}\}/g, pagespeedDesktop)
        .replace(/\{\{SEOZOOM_AUTHORITY\}\}/g, seozoomAuthority)
        .replace(/\{\{SEOZOOM_TRAFFIC\}\}/g, seozoomTraffic)
        .replace(/\{\{LINK_ANALISI_SITO\}\}/g, analysisLink);
        // Note: {{LINK_VIDEO}}, {{LINK_CONSULENZA}}, {{LINK_RISORSA}}, {{LINK_CHIAMAMI}}, {{LINK_TRACKING}}
        // are NOT replaced here - backend will replace them with tracked URLs
      
      // Use the new endpoint with tracking
      await axios.post(`${API}/members/send-email`, {
        member_id: memberId,
        to_email: member.email,
        subject,
        body,
        chapter_id: chapter?.chapter_id,
        member_name: member.name,
        custom_links: customLinks,
        base_url: window.location.origin.replace('-preview.', '-').replace(':3000', '')
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Track the action
      await trackMarketingAction(member, 'email', stage);
      
      toast.success(`Email inviata a ${member.email}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'invio dell'email di test"));
    } finally {
      setIsSendingEmail(null);
    }
  };
  
  const getTemplatePreview = (template) => {
    const calendarLink = settings?.calendar_link_bni || settings?.calendar_link || "https://calendar.google.com/...";
    return template
      .replace(/\{\{NOME\}\}/g, "Mario")
      .replace(/\{\{COGNOME\}\}/g, "Rossi")
      .replace(/\{\{EMAIL\}\}/g, "mario.rossi@esempio.it")
      .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || "Capitolo BNI")
      .replace(/\{\{LINK_CALENDAR_BNI\}\}/g, calendarLink)
      .replace(/\{\{LINK_CALENDAR_STANDARD\}\}/g, settings?.calendar_link_standard || calendarLink)
      .replace(/\{\{LINK_CALENDAR\}\}/g, calendarLink)
      .replace(/\{\{PAGESPEED_MOBILE\}\}/g, "85")
      .replace(/\{\{PAGESPEED_DESKTOP\}\}/g, "92")
      .replace(/\{\{SEOZOOM_AUTHORITY\}\}/g, "32")
      .replace(/\{\{SEOZOOM_TRAFFIC\}\}/g, "1.250")
      .replace(/\{\{LINK_ANALISI_SITO\}\}/g, "https://pagespeed.web.dev/report?url=...");
  };
  
  // Handle member field editing
  const handleStartEdit = (member, field) => {
    const memberId = member.encrypted_member_id || member.member_id;
    setEditingMember({
      id: memberId,
      field,
      value: member[field] || ""
    });
  };
  
  const handleCancelEdit = () => {
    setEditingMember(null);
  };
  
  const handleSaveMemberField = async () => {
    if (!editingMember) return;
    
    setIsSavingMember(true);
    try {
      const payload = { 
        member_id: editingMember.id,
        [editingMember.field]: editingMember.value,
        chapter_id: chapter.chapter_id
      };
      
      console.log("Saving member:", payload);
      
      // Use POST with member_id in body to avoid URL encoding issues
      await axios.post(`${API}/member-update`, payload, { headers: { Authorization: `Bearer ${token}` } });
      
      // Update local state
      setMembers(prev => prev.map(m => {
        const mId = m.encrypted_member_id || m.member_id;
        if (mId === editingMember.id) {
          return { ...m, [editingMember.field]: editingMember.value };
        }
        return m;
      }));
      
      toast.success(`${editingMember.field === 'email' ? 'Email' : 'Sito web'} aggiornato`);
      setEditingMember(null);
    } catch (error) {
      console.error("Error saving member:", error);
      console.error("Response:", error.response?.data);
      toast.error(getErrorMessage(error, "Errore nel salvataggio"));
    } finally {
      setIsSavingMember(false);
    }
  };

  useEffect(() => {
    loadChapterDetail();
    loadSettings();
  }, [chapterUrl, token]);

  // Check for existing extraction job on page load
  useEffect(() => {
    if (!chapter?.chapter_id || !token) return;
    
    const savedJobId = localStorage.getItem(`extraction_job_${chapter.chapter_id}`);
    if (savedJobId) {
      // Resume polling for existing job
      setIsExtractingData(true);
      setExtractionProgress({ message: "Ripresa estrazione in corso...", processed: 0, total: 0, emails: 0, websites: 0 });
      resumeExtractionPolling(savedJobId);
    }
  }, [chapter?.chapter_id, token]);

  const resumeExtractionPolling = async (jobId) => {
    try {
      const statusResponse = await axios.get(
        `${API}/members/extract-status/${encodeURIComponent(jobId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const status = statusResponse.data;
      
      if (status.status === 'completed') {
        localStorage.removeItem(`extraction_job_${chapter.chapter_id}`);
        if (status.members) {
          setMembers(status.members);
        }
        toast.success(`Estrazione completata: ${status.extracted_emails || 0} email e ${status.extracted_websites || 0} siti web`);
        setIsExtractingData(false);
        setExtractionProgress(null);
        loadChapterDetail(); // Reload data
      } else if (status.status === 'error' || status.status === 'not_found') {
        localStorage.removeItem(`extraction_job_${chapter.chapter_id}`);
        toast.info("Nessuna estrazione attiva trovata");
        setIsExtractingData(false);
        setExtractionProgress(null);
      } else {
        // Still running, continue polling
        setExtractionProgress({
          message: status.message,
          processed: status.processed || 0,
          total: status.total_members || 0,
          emails: status.extracted_emails || 0,
          websites: status.extracted_websites || 0
        });
        setTimeout(() => resumeExtractionPolling(jobId), 2000);
      }
    } catch (error) {
      console.error("Error resuming extraction:", error);
      localStorage.removeItem(`extraction_job_${chapter?.chapter_id}`);
      setIsExtractingData(false);
      setExtractionProgress(null);
    }
  };

  const handleExtractData = async () => {
    if (!chapter?.chapter_id) return;

    setIsExtractingData(true);
    setExtractionProgress({ message: "Avvio estrazione...", processed: 0, total: 0, emails: 0, websites: 0 });
    
    try {
      // Start the extraction job
      const startResponse = await axios.post(
        `${API}/members/extract-data`,
        { chapter_id: chapter.chapter_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const jobId = startResponse.data.job_id;
      
      if (!jobId) {
        throw new Error("Nessun job ID ricevuto");
      }

      // Save job ID to localStorage for resume capability
      localStorage.setItem(`extraction_job_${chapter.chapter_id}`, jobId);

      // Poll for status
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/members/extract-status/${encodeURIComponent(jobId)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const status = statusResponse.data;
          
          setExtractionProgress({
            message: status.message,
            processed: status.processed || 0,
            total: status.total_members || 0,
            emails: status.extracted_emails || 0,
            websites: status.extracted_websites || 0
          });

          if (status.status === 'completed') {
            localStorage.removeItem(`extraction_job_${chapter.chapter_id}`);
            if (status.members) {
              setMembers(status.members);
            }
            toast.success(
              `Estratti ${status.extracted_emails || 0} email e ${status.extracted_websites || 0} siti web`
            );
            setIsExtractingData(false);
            setExtractionProgress(null);
          } else if (status.status === 'error') {
            localStorage.removeItem(`extraction_job_${chapter.chapter_id}`);
            toast.error(status.message || "Errore nell'estrazione");
            setIsExtractingData(false);
            setExtractionProgress(null);
          } else {
            // Still running, poll again in 2 seconds
            setTimeout(pollStatus, 2000);
          }
        } catch (pollError) {
          console.error("Error polling status:", pollError);
          // If polling fails, retry after a longer delay
          setTimeout(pollStatus, 5000);
        }
      };

      // Start polling after a brief delay
      setTimeout(pollStatus, 1000);
      
    } catch (error) {
      console.error("Error starting extraction:", error);
      toast.error("Errore nell'avvio dell'estrazione");
      setIsExtractingData(false);
      setExtractionProgress(null);
    }
  };

  const handleExportCSV = async () => {
    if (!chapter?.chapter_id) return;

    try {
      const response = await axios.post(
        `${API}/members/export`,
        { chapter_id: chapter.chapter_id },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `membri_${chapter?.name?.replace(/\s+/g, '_') || 'capitolo'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("CSV esportato con successo");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Errore nell'esportazione");
    }
  };

  // Count members with extracted data
  const extractedCount = members.filter(m => m.data_extracted).length;
  const membersWithEmail = members.filter(m => m.email).length;

  // Helper to check if website is valid
  const isValidWebsite = (website) => {
    if (!website) return false;
    if (!website.includes('.')) return false;  // Must have a domain
    const placeholders = ['successnet.it', 'example.com', 'test.com'];
    return !placeholders.some(p => website.toLowerCase().includes(p));
  };

  // Helper to format address
  const formatAddress = (member) => {
    const parts = [member.address, member.city, member.postal_code].filter(Boolean);
    return parts.join(', ');
  };

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="spinner mb-4" />
          <p className="text-muted-foreground">Caricamento capitolo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/chapters">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Capitoli
                </Button>
              </Link>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-foreground">
                  {chapter?.name || "Dettaglio Capitolo"}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={members.length === 0}
                className="gap-2"
                data-testid="export-csv-button"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Esporta CSV</span>
              </Button>
              
              <Button
                onClick={handleExtractData}
                disabled={isExtractingData || !chapter?.chapter_id}
                className="gap-2"
                data-testid="extract-members-data-button"
              >
                {isExtractingData ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">
                      {extractionProgress ? 
                        `${extractionProgress.processed}/${extractionProgress.total || '?'}` : 
                        'Avvio...'}
                    </span>
                  </>
                ) : (
                  <>
                    <DatabaseZap className="h-4 w-4" />
                    <span className="hidden sm:inline">Estrai Dati</span>
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Extraction Progress Banner */}
          {isExtractingData && extractionProgress && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    {extractionProgress.message}
                  </p>
                  <div className="flex gap-4 mt-1 text-xs text-blue-600 dark:text-blue-400">
                    <span>Email trovate: {extractionProgress.emails}</span>
                    <span>Siti web trovati: {extractionProgress.websites}</span>
                  </div>
                </div>
                {extractionProgress.total > 0 && (
                  <div className="text-right">
                    <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                      {Math.round((extractionProgress.processed / extractionProgress.total) * 100)}%
                    </span>
                  </div>
                )}
              </div>
              {extractionProgress.total > 0 && (
                <div className="mt-2 w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(extractionProgress.processed / extractionProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Chapter Info */}
        {chapter && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="border-border/60">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Capitolo</p>
                      <p className="font-semibold">{chapter.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Riunione</p>
                      <p className="font-semibold">{chapter.meeting_day} {chapter.meeting_time}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Membri</p>
                      <p className="font-semibold">{members.length}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Con Email</p>
                      <p className="font-semibold">{membersWithEmail}/{members.length}</p>
                    </div>
                  </div>
                </div>
                
                {chapter.location_name && (
                  <div className="mt-4 pt-4 border-t border-border/60 flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{chapter.location_name}</span>
                    {chapter.address && <span>- {chapter.address}</span>}
                    {chapter.city && <span>, {chapter.city}</span>}
                  </div>
                )}
                
                {/* Template Selectors in Header */}
                <div className="mt-4 pt-4 border-t border-border/60">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground">Template:</span>
                    
                    {/* Email/WhatsApp Toggle */}
                    <div className="flex gap-1 bg-muted rounded-lg p-1">
                      <Button
                        variant={activeTemplateType === "email" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setActiveTemplateType("email")}
                        className="h-7 px-3 gap-1"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Email
                      </Button>
                      <Button
                        variant={activeTemplateType === "whatsapp" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setActiveTemplateType("whatsapp")}
                        className="h-7 px-3 gap-1"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                        WhatsApp
                      </Button>
                    </div>
                    
                    {/* Stage Selector */}
                    <div className="flex gap-1">
                      {[
                        { key: "first", label: "1°", color: "bg-blue-500 hover:bg-blue-600" },
                        { key: "second", label: "2°", color: "bg-green-500 hover:bg-green-600" },
                        { key: "third", label: "3°", color: "bg-red-500 hover:bg-red-600" }
                      ].map(({ key, label, color }) => (
                        <Button
                          key={key}
                          variant={activeTemplateStage === key ? "default" : "outline"}
                          size="sm"
                          onClick={() => setActiveTemplateStage(key)}
                          className={`h-7 w-8 p-0 ${activeTemplateStage === key ? color : ''}`}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Edit Template Link */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab("templates")}
                      className="h-7 px-2 gap-1 text-muted-foreground hover:text-foreground"
                    >
                      <FileCode className="h-3.5 w-3.5" />
                      Modifica
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="members" className="gap-2" data-testid="members-tab">
              <Users className="h-4 w-4" />
              Membri
            </TabsTrigger>
            <TabsTrigger value="marketing" className="gap-2" data-testid="marketing-tab">
              <Megaphone className="h-4 w-4" />
              Marketing
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2" data-testid="templates-tab">
              <FileCode className="h-4 w-4" />
              Template
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2" data-testid="analytics-tab" onClick={() => !analytics && loadAnalytics()}>
              <BarChart2 className="h-4 w-4" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            {members.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun membro trovato</p>
                </CardContent>
              </Card>
            ) : (
              <TooltipProvider delayDuration={200}>
                <Card className="border-border/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table className="table-fixed w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold w-[140px]">Nome</TableHead>
                          <TableHead className="font-semibold w-[120px]">Società</TableHead>
                          <TableHead className="font-semibold w-[100px]">Specializzazione</TableHead>
                          <TableHead className="font-semibold w-[100px]">Telefono</TableHead>
                          <TableHead className="font-semibold w-[150px]">Email</TableHead>
                          <TableHead className="font-semibold w-[120px]">Sito Web</TableHead>
                          <TableHead className="font-semibold w-[80px]">Città</TableHead>
                          <TableHead className="font-semibold w-[80px]">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map((member, index) => (
                          <TableRow key={member.id || index} className="hover:bg-muted/50">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-1 truncate" title={member.name}>
                                <span className="truncate">{member.name}</span>
                                {member.data_extracted && (
                                  <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground truncate" title={member.company}>{member.company || "-"}</TableCell>
                            <TableCell className="text-muted-foreground truncate" title={member.specialization}>{member.specialization || "-"}</TableCell>
                            <TableCell className="truncate">
                              {member.phone ? (
                                <a href={`tel:${member.phone}`} className="hover:text-primary text-sm">{member.phone}</a>
                              ) : "-"}
                            </TableCell>
                            <TableCell>
                              {editingMember?.id === (member.encrypted_member_id || member.member_id) && editingMember?.field === 'email' ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    value={editingMember.value}
                                    onChange={(e) => setEditingMember(prev => ({ ...prev, value: e.target.value }))}
                                    className="h-6 text-xs w-[120px]"
                                    placeholder="email@esempio.it"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveMemberField();
                                      if (e.key === 'Escape') handleCancelEdit();
                                    }}
                                  />
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                                    onClick={handleSaveMemberField}
                                    disabled={isSavingMember}
                                  >
                                    {isSavingMember ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                    onClick={handleCancelEdit}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 group">
                                  {member.email ? (
                                    <a href={`mailto:${member.email}`} className="hover:text-primary truncate text-xs block max-w-[110px]" title={member.email}>
                                      {member.email}
                                    </a>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                    onClick={() => handleStartEdit(member, 'email')}
                                  >
                                    <Pencil className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                            {/* Website Column - Editable */}
                            <TableCell>
                              {editingMember?.id === (member.encrypted_member_id || member.member_id) && editingMember?.field === 'website' ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    value={editingMember.value}
                                    onChange={(e) => setEditingMember(prev => ({ ...prev, value: e.target.value }))}
                                    className="h-6 text-xs w-[100px]"
                                    placeholder="www.esempio.it"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveMemberField();
                                      if (e.key === 'Escape') handleCancelEdit();
                                    }}
                                  />
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
                                    onClick={handleSaveMemberField}
                                    disabled={isSavingMember}
                                  >
                                    {isSavingMember ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                                    onClick={handleCancelEdit}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 group">
                                  {member.website ? (
                                    <a 
                                      href={member.website.startsWith('http') ? member.website : `https://${member.website}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="hover:text-primary truncate text-xs block max-w-[90px]" 
                                      title={member.website}
                                    >
                                      {normalizeDomain(member.website)}
                                    </a>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                    onClick={() => handleStartEdit(member, 'website')}
                                  >
                                    <Pencil className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs truncate">{member.city || "-"}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {member.phone && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild>
                                        <a href={`tel:${member.phone}`}><Phone className="h-4 w-4" /></a>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{member.phone}</p></TooltipContent>
                                  </Tooltip>
                                )}
                                {member.email && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild>
                                        <a href={`mailto:${member.email}`}><Mail className="h-4 w-4" /></a>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{member.email}</p></TooltipContent>
                                  </Tooltip>
                                )}
                                {isValidWebsite(member.website) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild>
                                        <a href={member.website} target="_blank" rel="noopener noreferrer"><Globe className="h-4 w-4" /></a>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{member.website}</p></TooltipContent>
                                  </Tooltip>
                                )}
                                {(member.address || member.city) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
                                        <Home className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p className="font-medium">Indirizzo</p>
                                      <p className="text-sm">{formatAddress(member) || "Non disponibile"}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </TooltipProvider>
            )}
          </TabsContent>

          {/* Templates Tab - BNI Email & WhatsApp Templates */}
          <TabsContent value="templates" className="space-y-4">
            {/* Template Type Selector */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={activeTemplateType === "email" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTemplateType("email")}
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                Email
              </Button>
              <Button
                variant={activeTemplateType === "whatsapp" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTemplateType("whatsapp")}
                className="gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </Button>
            </div>
            
            {/* Stage Selector */}
            <div className="flex gap-2 mb-4">
              {[
                { key: "first", label: "Prima", color: "bg-blue-100 text-blue-800" },
                { key: "second", label: "Seconda", color: "bg-green-100 text-green-800" },
                { key: "third", label: "Terza", color: "bg-red-100 text-red-800" }
              ].map(({ key, label, color }) => (
                <Button
                  key={key}
                  variant={activeTemplateStage === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveTemplateStage(key)}
                  className="gap-2"
                >
                  <Badge className={color}>{label}</Badge>
                  {activeTemplateType === "email" ? "Email" : "Messaggio"}
                </Button>
              ))}
            </div>
            
            {activeTemplateType === "email" ? (
              /* Email Template Editor */
              <Card className="border-border/60">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-primary" />
                        Template Email BNI - {activeTemplateStage === 'first' ? 'Prima' : activeTemplateStage === 'second' ? 'Seconda' : 'Terza'}
                      </CardTitle>
                      <CardDescription>
                        {activeTemplateStage === 'first' && "Prima email di contatto"}
                        {activeTemplateStage === 'second' && "Follow-up dopo la prima email"}
                        {activeTemplateStage === 'third' && "Ultimo tentativo di contatto"}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBniEmailTemplates(prev => ({
                            ...prev,
                            [activeTemplateStage]: DEFAULT_BNI_EMAIL_TEMPLATES[activeTemplateStage]
                          }));
                          setBniEmailSubjects(prev => ({
                            ...prev,
                            [activeTemplateStage]: DEFAULT_BNI_EMAIL_SUBJECTS[activeTemplateStage]
                          }));
                          toast.info("Template ripristinato");
                        }}
                        className="gap-1"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Reset
                      </Button>
                      <Button
                        onClick={handleSaveBniTemplates}
                        disabled={isSavingTemplates}
                        className="gap-1"
                      >
                        {isSavingTemplates ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Salva
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Subject */}
                  <div className="space-y-2">
                    <Label>Oggetto Email</Label>
                    <Input
                      value={bniEmailSubjects[activeTemplateStage]}
                      onChange={(e) => setBniEmailSubjects(prev => ({ ...prev, [activeTemplateStage]: e.target.value }))}
                      placeholder="Oggetto dell'email..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Editor */}
                    <div className="space-y-2">
                      <Label>Codice HTML</Label>
                      <Textarea
                        value={bniEmailTemplates[activeTemplateStage]}
                        onChange={(e) => setBniEmailTemplates(prev => ({ ...prev, [activeTemplateStage]: e.target.value }))}
                        className="min-h-[400px] font-mono text-sm"
                        placeholder="Inserisci il template HTML..."
                      />
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>Anteprima</Label>
                      <div 
                        className="border rounded-lg p-4 min-h-[400px] bg-white overflow-auto"
                        dangerouslySetInnerHTML={{ __html: getTemplatePreview(bniEmailTemplates[activeTemplateStage]) }}
                      />
                    </div>
                  </div>
                  
                  {/* Test Email Section */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="mb-2 block">Invia Email di Test</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@esempio.it"
                        value={testEmailRecipient}
                        onChange={(e) => setTestEmailRecipient(e.target.value)}
                        className="max-w-xs"
                      />
                      <Button
                        variant="outline"
                        onClick={handleSendTestBniEmail}
                        disabled={isSendingTestEmail}
                      >
                        {isSendingTestEmail ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Invia Test
                      </Button>
                    </div>
                  </div>
                  
                  {/* Placeholders Info */}
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <p className="font-medium mb-1">Placeholder disponibili:</p>
                    <code className="text-xs">{"{{NOME}}"}</code>, <code className="text-xs">{"{{COGNOME}}"}</code>, <code className="text-xs">{"{{CAPITOLO}}"}</code>, <code className="text-xs">{"{{LINK_CALENDAR_BNI}}"}</code>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* WhatsApp Template Editor */
              <Card className="border-border/60">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-green-600" />
                        Template WhatsApp BNI - {activeTemplateStage === 'first' ? 'Primo' : activeTemplateStage === 'second' ? 'Secondo' : 'Terzo'}
                      </CardTitle>
                      <CardDescription>
                        {activeTemplateStage === 'first' && "Primo messaggio di contatto"}
                        {activeTemplateStage === 'second' && "Follow-up dopo il primo messaggio"}
                        {activeTemplateStage === 'third' && "Ultimo tentativo di contatto"}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBniWhatsappTemplates(prev => ({
                            ...prev,
                            [activeTemplateStage]: DEFAULT_BNI_WHATSAPP_TEMPLATES[activeTemplateStage]
                          }));
                          toast.info("Template ripristinato");
                        }}
                        className="gap-1"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Reset
                      </Button>
                      <Button
                        onClick={handleSaveBniTemplates}
                        disabled={isSavingTemplates}
                        className="gap-1"
                      >
                        {isSavingTemplates ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Salva
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Editor */}
                    <div className="space-y-2">
                      <Label>Messaggio</Label>
                      <Textarea
                        value={bniWhatsappTemplates[activeTemplateStage]}
                        onChange={(e) => setBniWhatsappTemplates(prev => ({ ...prev, [activeTemplateStage]: e.target.value }))}
                        className="min-h-[300px]"
                        placeholder="Scrivi il messaggio WhatsApp..."
                      />
                    </div>

                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>Anteprima</Label>
                      <div className="bg-[#e5ddd5] rounded-lg p-4 min-h-[300px]">
                        <div className="bg-[#dcf8c6] rounded-lg p-3 max-w-[90%] ml-auto shadow-sm">
                          <p className="text-sm whitespace-pre-wrap">
                            {getTemplatePreview(bniWhatsappTemplates[activeTemplateStage])}
                          </p>
                          <p className="text-[10px] text-gray-500 text-right mt-1">
                            12:34 ✓✓
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Placeholders Info */}
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <p className="font-medium mb-1">Placeholder disponibili:</p>
                    <code className="text-xs">{"{{NOME}}"}</code>, <code className="text-xs">{"{{COGNOME}}"}</code>, <code className="text-xs">{"{{CAPITOLO}}"}</code>, <code className="text-xs">{"{{LINK_CALENDAR_BNI}}"}</code>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Custom Links Section */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Link Personalizzati per Email
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Configura i link che verranno tracciati nelle email. Il campo "Tracking" serve per il pixel esterno (non tracciato come click).
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tracking (Pixel esterno)</label>
                    <Input
                      value={customLinks.tracking}
                      onChange={(e) => setCustomLinks({...customLinks, tracking: e.target.value})}
                      placeholder="<img src='...' /> o URL"
                    />
                    <p className="text-xs text-muted-foreground">Usa <code>{`{{LINK_TRACKING}}`}</code> nel template</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Video</label>
                    <Input
                      value={customLinks.video}
                      onChange={(e) => setCustomLinks({...customLinks, video: e.target.value})}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                    <p className="text-xs text-muted-foreground">Usa <code>{`{{LINK_VIDEO}}`}</code> nel template</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Consulenza</label>
                    <Input
                      value={customLinks.consulenza}
                      onChange={(e) => setCustomLinks({...customLinks, consulenza: e.target.value})}
                      placeholder="https://calendly.com/..."
                    />
                    <p className="text-xs text-muted-foreground">Usa <code>{`{{LINK_CONSULENZA}}`}</code> nel template</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Risorsa</label>
                    <Input
                      value={customLinks.risorsa}
                      onChange={(e) => setCustomLinks({...customLinks, risorsa: e.target.value})}
                      placeholder="https://drive.google.com/..."
                    />
                    <p className="text-xs text-muted-foreground">Usa <code>{`{{LINK_RISORSA}}`}</code> nel template</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Chiamami</label>
                    <Input
                      value={customLinks.chiamami}
                      onChange={(e) => setCustomLinks({...customLinks, chiamami: e.target.value})}
                      placeholder="https://wa.me/..."
                    />
                    <p className="text-xs text-muted-foreground">Usa <code>{`{{LINK_CHIAMAMI}}`}</code> nel template</p>
                  </div>
                </div>
                <Button onClick={handleSaveCustomLinks} className="mt-4">
                  <Save className="h-4 w-4 mr-2" />
                  Salva Link
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            {members.length === 0 ? (
              <Card className="border-border/60">
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun membro trovato. Estrai prima i dati del capitolo.</p>
                </CardContent>
              </Card>
            ) : !settings?.smtp_settings?.host ? (
              <Card className="border-border/60">
                <CardContent className="py-12 text-center">
                  <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Configura SMTP nelle Impostazioni per inviare email reali</p>
                  <Link to="/settings">
                    <Button>Configura SMTP</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <TooltipProvider delayDuration={200}>
                {/* SEO Legend */}
                <Card className="border-border/60 mb-4">
                  <CardContent className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-6 text-sm">
                        <span className="font-semibold text-muted-foreground">Legenda SEO:</span>
                        <div className="flex items-center gap-1.5">
                          <Smartphone className="h-4 w-4 text-blue-600" />
                          <span>Mobile</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Monitor className="h-4 w-4 text-purple-600" />
                          <span>Desktop</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <TrendingUp className="h-4 w-4 text-green-600" />
                          <span>Traffico</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Search className="h-4 w-4 text-orange-600" />
                          <span>Keywords</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Zap className="h-4 w-4 text-yellow-600" />
                          <span>Top 1-3</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Award className="h-4 w-4 text-red-600" />
                          <span>Authority</span>
                        </div>
                      </div>
                      <Button
                        onClick={analyzeAllWebsites}
                        disabled={isAnalyzingAll}
                        size="sm"
                        variant="outline"
                        className="gap-2"
                      >
                        {isAnalyzingAll ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Analisi in corso...
                          </>
                        ) : (
                          <>
                            <BarChart3 className="h-4 w-4" />
                            Analizza Tutti i Siti
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold">Nome</TableHead>
                          <TableHead className="font-semibold">Specializzazione</TableHead>
                          <TableHead className="font-semibold text-center w-24">Contatti</TableHead>
                          <TableHead className="font-semibold text-center w-24">
                            <div className="flex flex-col items-center">
                              <span>Invii</span>
                              <span className="text-xs font-normal text-muted-foreground">Email / WA</span>
                            </div>
                          </TableHead>
                          <TableHead className="font-semibold bg-green-50 text-center" style={{minWidth: '200px'}}>
                            <div className="flex items-center justify-center gap-1">
                              <BarChart3 className="h-4 w-4" />
                              Dati SEO
                            </div>
                          </TableHead>
                          <TableHead className="font-semibold w-40 text-center">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map((member, index) => {
                          const whatsappLink = getWhatsAppLink(member);
                          const emailLink = getEmailLink(member);
                          const phoneFormatted = standardizePhoneForWhatsApp(member.phone);
                          const domain = normalizeDomain(member.website);
                          const seo = seoData[domain] || {};
                          const isAnalyzing = analyzingMember === domain;
                          const hasValidSeoData = hasSeoData(seo);
                          const hasErrors = hasSeoErrors(seo);
                          const memberId = member.encrypted_member_id || member.member_id || member.id;
                          const memberStats = marketingStats[memberId] || {};
                          const emailCount = memberStats.email_count || 0;
                          const whatsappCount = memberStats.whatsapp_count || 0;
                          
                          return (
                            <TableRow key={member.id || index} className="hover:bg-muted/50">
                              <TableCell className="font-medium">{member.name}</TableCell>
                              <TableCell className="text-muted-foreground max-w-[180px] truncate" title={member.specialization}>
                                {getShortSpecialization(member.specialization)}
                              </TableCell>
                              
                              {/* Compact Contacts Column */}
                              <TableCell>
                                <div className="flex items-center justify-center gap-1">
                                  {/* Phone Icon */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className={`h-7 w-7 rounded flex items-center justify-center ${phoneFormatted ? 'bg-blue-100 cursor-pointer hover:bg-blue-200' : 'bg-gray-100 opacity-40'}`}>
                                        {phoneFormatted ? (
                                          <a href={`tel:${member.phone}`}>
                                            <Phone className="h-3.5 w-3.5 text-blue-600" />
                                          </a>
                                        ) : (
                                          <Phone className="h-3.5 w-3.5 text-gray-400" />
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{phoneFormatted || "Telefono non disponibile"}</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  {/* Email Icon */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className={`h-7 w-7 rounded flex items-center justify-center ${member.email ? 'bg-purple-100 cursor-pointer hover:bg-purple-200' : 'bg-gray-100 opacity-40'}`}>
                                        {member.email ? (
                                          <a href={`mailto:${member.email}`}>
                                            <Mail className="h-3.5 w-3.5 text-purple-600" />
                                          </a>
                                        ) : (
                                          <Mail className="h-3.5 w-3.5 text-gray-400" />
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{member.email || "Email non disponibile"}</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  {/* Website Icon */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className={`h-7 w-7 rounded flex items-center justify-center ${isValidWebsite(member.website) ? 'bg-green-100 cursor-pointer hover:bg-green-200' : 'bg-gray-100 opacity-40'}`}>
                                        {isValidWebsite(member.website) ? (
                                          <a href={member.website} target="_blank" rel="noopener noreferrer">
                                            <Globe className="h-3.5 w-3.5 text-green-600" />
                                          </a>
                                        ) : (
                                          <Globe className="h-3.5 w-3.5 text-gray-400" />
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{isValidWebsite(member.website) ? domain : "Sito non disponibile"}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableCell>

                              {/* Invii Count Column */}
                              <TableCell>
                                <div className="flex items-center justify-center gap-2">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge 
                                        variant={emailCount > 0 ? "default" : "outline"} 
                                        className={`${emailCount > 0 ? 'bg-blue-600' : ''}`}
                                      >
                                        <Mail className="h-3 w-3 mr-1" />
                                        {emailCount}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{emailCount} email inviate</p>
                                      {memberStats.email_last_stage && (
                                        <p className="text-xs text-muted-foreground">
                                          Ultimo: {memberStats.email_last_stage === 'first' ? '1°' : memberStats.email_last_stage === 'second' ? '2°' : '3°'}
                                        </p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge 
                                        variant={whatsappCount > 0 ? "default" : "outline"}
                                        className={`${whatsappCount > 0 ? 'bg-green-600' : ''}`}
                                      >
                                        <MessageCircle className="h-3 w-3 mr-1" />
                                        {whatsappCount}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{whatsappCount} WhatsApp inviati</p>
                                      {memberStats.whatsapp_last_stage && (
                                        <p className="text-xs text-muted-foreground">
                                          Ultimo: {memberStats.whatsapp_last_stage === 'first' ? '1°' : memberStats.whatsapp_last_stage === 'second' ? '2°' : '3°'}
                                        </p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                              </TableCell>
                              
                              {/* SEO Data Column */}
                              <TableCell className="bg-green-50/50">
                                {!isValidWebsite(member.website) ? (
                                  <div className="text-center text-muted-foreground text-xs">-</div>
                                ) : isAnalyzing ? (
                                  <div className="flex items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                  </div>
                                ) : hasErrors ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-orange-500 text-xs cursor-help">Non raggiungibile</span>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs">
                                        <p className="text-xs">{seo.errors?.join(', ') || 'Errore sconosciuto'}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs px-2"
                                      onClick={() => analyzeSingleWebsite(member.website, true)}
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : !seo.domain || !hasValidSeoData ? (
                                  <div className="flex items-center justify-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() => analyzeSingleWebsite(member.website, true)}
                                    >
                                      <BarChart3 className="h-3 w-3 mr-1" />
                                      {seo.domain ? "Rianalizza" : "Analizza"}
                                    </Button>
                                  </div>
                                ) : seo.error ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <span className="text-red-500 text-sm">Errore</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs px-2"
                                      onClick={() => analyzeSingleWebsite(member.website, true)}
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center gap-3">
                                    {/* PageSpeed Mobile */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center min-w-[28px]">
                                          <Smartphone className={`h-4 w-4 ${getScoreColor(seo.pagespeed_mobile)}`} />
                                          <span className={`text-sm font-semibold ${getScoreColor(seo.pagespeed_mobile)}`}>
                                            {seo.pagespeed_mobile ?? "-"}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>PageSpeed Mobile: {seo.pagespeed_mobile ?? "N/A"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* PageSpeed Desktop */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center min-w-[28px]">
                                          <Monitor className={`h-4 w-4 ${getScoreColor(seo.pagespeed_desktop)}`} />
                                          <span className={`text-sm font-semibold ${getScoreColor(seo.pagespeed_desktop)}`}>
                                            {seo.pagespeed_desktop ?? "-"}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>PageSpeed Desktop: {seo.pagespeed_desktop ?? "N/A"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Traffic */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center min-w-[32px]">
                                          <TrendingUp className="h-4 w-4 text-green-600" />
                                          <span className="text-sm font-semibold text-green-700">
                                            {formatNumber(seo.traffic)}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Traffico mensile: {seo.traffic?.toLocaleString() ?? "N/A"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Keywords */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center min-w-[32px]">
                                          <Search className="h-4 w-4 text-orange-600" />
                                          <span className="text-sm font-semibold text-orange-700">
                                            {formatNumber(seo.keywords_total)}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Keywords totali: {seo.keywords_total?.toLocaleString() ?? "N/A"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Keywords Top 3 */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center min-w-[28px]">
                                          <Zap className="h-4 w-4 text-yellow-600" />
                                          <span className="text-sm font-semibold text-yellow-700">
                                            {formatNumber(seo.keywords_top3)}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Keywords posizione 1-3: {seo.keywords_top3?.toLocaleString() ?? "N/A"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Authority */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex flex-col items-center min-w-[28px]">
                                          <Award className="h-4 w-4 text-red-600" />
                                          <span className="text-sm font-semibold text-red-700">
                                            {seo.authority ?? "-"}
                                          </span>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Zoom Authority: {seo.authority ?? "N/A"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Refresh Button */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 ml-1 opacity-50 hover:opacity-100"
                                          onClick={() => analyzeSingleWebsite(member.website, true)}
                                        >
                                          <RefreshCw className="h-3 w-3" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Rianalizza</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                )}
                              </TableCell>
                              
                              {/* Actions Column */}
                              <TableCell>
                                <div className="flex items-center justify-center gap-1">
                                  {/* Email Buttons - 3 stages - Real SMTP sending */}
                                  {['first', 'second', 'third'].map((stage, idx) => {
                                    const stageNum = idx + 1;
                                    const isDisabled = !member.email;
                                    const memberId = member._id || member.member_id;
                                    const isCurrentlySending = isSendingEmail === memberId;
                                    
                                    return (
                                      <Tooltip key={`email-${stage}`}>
                                        <TooltipTrigger asChild>
                                          <Button 
                                            variant={isDisabled ? "ghost" : "outline"}
                                            size="sm" 
                                            className={`h-7 w-7 p-0 text-xs ${isDisabled ? 'opacity-30 cursor-not-allowed' : ''} ${
                                              stage === 'first' ? 'border-blue-300 hover:bg-blue-50' :
                                              stage === 'second' ? 'border-green-300 hover:bg-green-50' :
                                              'border-red-300 hover:bg-red-50'
                                            }`}
                                            disabled={isDisabled || isCurrentlySending}
                                            onClick={() => handleSendRealEmail(member, stage)}
                                          >
                                            {isCurrentlySending ? (
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : (
                                              <span className={`text-xs font-bold ${
                                                stage === 'first' ? 'text-blue-600' :
                                                stage === 'second' ? 'text-green-600' :
                                                'text-red-600'
                                              }`}>{stageNum}</span>
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{isDisabled ? "Email non disponibile" : `Invia Email ${stageNum}° via SMTP`}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    );
                                  })}
                                  
                                  <div className="w-px h-6 bg-border mx-1" />
                                  
                                  {/* WhatsApp Buttons - 3 stages */}
                                  {['first', 'second', 'third'].map((stage, idx) => {
                                    const stageNum = idx + 1;
                                    const isDisabled = !whatsappLink;
                                    const phone = standardizePhoneForWhatsApp(member.phone);
                                    const calendarLink = settings?.calendar_link_bni || settings?.calendar_link || '';
                                    const template = bniWhatsappTemplates[stage] || DEFAULT_BNI_WHATSAPP_TEMPLATES[stage];
                                    const message = template
                                      .replace(/\{\{NOME\}\}/g, member.name?.split(' ')[0] || '')
                                      .replace(/\{\{COGNOME\}\}/g, member.name?.split(' ').slice(1).join(' ') || '')
                                      .replace(/\{\{CAPITOLO\}\}/g, chapter?.name || '')
                                      .replace(/\{\{LINK_CALENDAR_BNI\}\}/g, calendarLink)
                                      .replace(/\{\{LINK_CALENDAR\}\}/g, calendarLink);
                                    const stageWhatsappLink = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : null;
                                    
                                    return (
                                      <Tooltip key={`wa-${stage}`}>
                                        <TooltipTrigger asChild>
                                          <Button 
                                            variant={isDisabled ? "ghost" : "outline"}
                                            size="sm" 
                                            className={`h-7 w-7 p-0 text-xs ${isDisabled ? 'opacity-30 cursor-not-allowed' : ''} ${
                                              stage === 'first' ? 'border-blue-300 hover:bg-blue-50' :
                                              stage === 'second' ? 'border-green-300 hover:bg-green-50' :
                                              'border-red-300 hover:bg-red-50'
                                            }`}
                                            disabled={isDisabled}
                                            onClick={() => {
                                              if (stageWhatsappLink) {
                                                window.open(stageWhatsappLink, '_blank');
                                                trackMarketingAction(member, 'whatsapp', stage);
                                              }
                                            }}
                                          >
                                            <MessageCircle className={`h-3 w-3 ${
                                              stage === 'first' ? 'text-blue-600' :
                                              stage === 'second' ? 'text-green-600' :
                                              'text-red-600'
                                            }`} />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{isDisabled ? "Telefono non disponibile" : `Invia WhatsApp ${stageNum}° (${
                                            stage === 'first' ? 'Primo' : stage === 'second' ? 'Secondo' : 'Terzo'
                                          })`}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </TooltipProvider>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard
              analytics={analytics}
              isLoading={isLoadingAnalytics}
              onRefresh={loadAnalytics}
              onExport={exportAnalytics}
              entityName="Membro"
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ChapterDetailPage;
